import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import Loader from 'components/Loader';
import PluginUnauthorized from '@/pages/plugin/PluginUnauthorized';
import { getUrl } from '../utils.jsx';
import { PLUGIN_PAGE_NAME, LIBRARY_PAGE_NAME } from '@/constants/globalConstants';

// ==============================|| PLUGIN AUTH GUARD ||============================== //
// Entry point for /<subroot>/plugin?token=...&plugin_key=... (the backend session of the
// plugin, from its /auth/login, and that session's plugin_key). loginWithToken checks
// with the backend that they match a connected plugin, uses that same session as the app
// session and loads /user/me — on success it redirects to
// /<subroot>/plugin/library, on failure it renders the inline "no permission" screen
// (with a retry button that re-runs this same check) instead of navigating away.

export default function PluginAuthGuard() {
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // 'checking' | 'invalid'
  const [failureReason, setFailureReason] = useState(null);
  const activeRef = useRef(true);

  const validate = useCallback(async () => {
    setStatus('checking');
    setFailureReason(null);
    const token = searchParams.get('token');
    const pluginKey = searchParams.get('plugin_key');
    const { success, reason } = await loginWithToken(token, pluginKey);

    if (!activeRef.current) return;

    if (success) {
      const libraryUrl = getUrl(location.pathname, `${PLUGIN_PAGE_NAME}/${LIBRARY_PAGE_NAME}`);
      navigate(libraryUrl, { replace: true });
    } else {
      setFailureReason(reason || null);
      setStatus('invalid');
    }
  }, [searchParams, loginWithToken, location, navigate]);

  useEffect(() => {
    activeRef.current = true;
    validate();

    return () => {
      activeRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (status === 'invalid') return <PluginUnauthorized onRetry={validate} reason={failureReason} />;

  return <Loader />;
}
